import React, { useEffect, useRef, useState } from "react";
import ClassNames from "Helpers/Common";
import { useVisitTypes } from "CustomHooks/useVisitTypes";

/**
 * Compact matrix cell for a doctor's enabledVisitTypes.
 * Value semantics (mirrors backend): null = all visit types, [] = none,
 * array = exactly those slugs. Renders a summary chip ("All", "None",
 * "3 of 7"); clicking it opens a checkbox popover with an "All types"
 * master checkbox. A full explicit selection collapses back to null.
 */
export const VisitTypesCell = ({ value, disabled, onChange }) => {
  const { data: allVisitTypes = [] } = useVisitTypes();
  // Only ACTIVE types are patient-creatable, so only those are meaningful to
  // restrict a doctor from. The admin /visit-type/list also returns inactive
  // ones (needed by the Visit Types screen) — filter them out here.
  const visitTypes = allVisitTypes.filter((visitType) => visitType.active);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const allSlugs = visitTypes.map((visitType) => visitType.slug);
  const isAll = value === null || value === undefined;
  const selected = isAll ? allSlugs : value;

  const summary = isAll
    ? "All"
    : selected.length === 0
    ? "None"
    : `${selected.length} of ${allSlugs.length}`;

  const emit = (nextSlugs) => {
    // Everything checked = the "all" default, so new types stay enabled
    onChange(nextSlugs.length === allSlugs.length ? null : nextSlugs);
  };

  const toggleType = (slug) => {
    emit(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  };

  const toggleAll = () => {
    onChange(isAll || selected.length === allSlugs.length ? [] : null);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={ClassNames(
          "px-3 py-1 rounded-full text-xs font-medium border",
          disabled
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
            : isAll
            ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
            : selected.length === 0
            ? "bg-red-100 text-red-700 border-red-200 hover:bg-red-200"
            : "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
        )}
      >
        {summary}
      </button>

      {open && !disabled && (
        <div className="absolute z-20 mt-1 right-0 w-56 bg-white border border-gray-200 rounded-md shadow-lg py-2 text-left">
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-50 cursor-pointer border-b border-gray-100 mb-1">
            <input type="checkbox" checked={isAll || selected.length === allSlugs.length} onChange={toggleAll} />
            All types
          </label>
          {visitTypes.map((visitType) => (
            <label
              key={visitType.slug}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <input type="checkbox" checked={selected.includes(visitType.slug)} onChange={() => toggleType(visitType.slug)} />
              <span className="truncate">{visitType.name}</span>
            </label>
          ))}
          {visitTypes.length === 0 && <div className="px-3 py-1.5 text-sm text-gray-400">No visit types</div>}
        </div>
      )}
    </div>
  );
};
